import { useState } from 'react';

type FormData = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

type SubmitStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

const initialFormData: FormData = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
};

function Modal({
  isOpen,
  children
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-6 py-8">
      <div className="w-full max-w-2xl bg-white shadow-2xl">
        {children}
      </div>
    </div>
  );
}

export default function Contact() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setPrivacyAccepted(false);
  };

  const handleOpenConfirm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setErrorMessage('必須項目を入力してください。');
      setSubmitStatus('error');
      return;
    }

    if (!privacyAccepted) {
      setErrorMessage('プライバシーポリシーに同意してください。');
      setSubmitStatus('error');
      return;
    }

    setErrorMessage('');
    setSubmitStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);
      formBody.append('privacy', privacyAccepted ? '同意する' : '');

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formBody.toString()
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      resetForm();
      setSubmitStatus('success');
    } catch (error) {
      setErrorMessage('送信に失敗しました。しばらくしてからもう一度お試しください。');
      setSubmitStatus('error');
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-emerald-50/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-emerald-900 mb-4">
            お問い合わせ
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            内装工事に関するご相談は、お気軽にお問い合わせください。専門スタッフが丁寧に対応いたします。
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white p-8 shadow-lg">
            <form onSubmit={handleOpenConfirm} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="設計・監理サービス">設計・監理サービス</option>
                  <option value="材料選定コンサルティング">材料選定コンサルティング</option>
                  <option value="オンライン講座・セミナー">オンライン講座・セミナー</option>
                  <option value="トータルサポート">トータルサポート</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  メッセージ <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all resize-none text-sm"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 text-emerald-700 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
                <label htmlFor="privacy" className="text-sm text-gray-700 cursor-pointer">
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 hover:text-emerald-800 underline"
                  >
                    プライバシーポリシー
                  </a>
                  に同意します <span className="text-red-500">*</span>
                </label>
              </div>

              <button
                type="submit"
                className="w-full bg-emerald-700 text-white py-4 font-semibold hover:bg-emerald-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                入力内容を確認する
              </button>
            </form>
          </div>

          <div className="space-y-8">
            <div className="bg-white p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-emerald-900 mb-6">お問い合わせ先</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-emerald-900 mb-1">所在地</h4>
                    <p className="text-gray-600">埼玉県春日部市藤塚1897番地4</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                    <i className="ri-time-line text-xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-emerald-900 mb-1">営業時間</h4>
                    <p className="text-gray-600">平日 9:00 - 18:00</p>
                    <p className="text-gray-600 text-sm">（土日祝日は休業）</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-emerald-900 mb-6">アクセス</h3>
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3228.6473785419794!2d139.77700047625896!3d35.98004557249231!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6018bc042096561f%3A0x5ec17fcca0f977b5!2z44CSMzQ0LTAwMTEg5Z-8546J55yM5pil5pel6YOo5biC6Jek5aGa77yR77yY77yX!5e0!3m2!1sja!2sjp!4v1775555260322!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="合同会社フジワン 所在地"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={submitStatus === 'confirm'}>
        <div className="p-8">
          <h3 className="text-2xl font-bold text-emerald-900 mb-6">入力内容の確認</h3>
          <div className="space-y-4 text-sm text-gray-700">
            <div>
              <p className="font-semibold text-emerald-900">お名前</p>
              <p>{formData.name}</p>
            </div>
            <div>
              <p className="font-semibold text-emerald-900">メールアドレス</p>
              <p>{formData.email}</p>
            </div>
            <div>
              <p className="font-semibold text-emerald-900">電話番号</p>
              <p>{formData.phone || '未入力'}</p>
            </div>
            <div>
              <p className="font-semibold text-emerald-900">お問い合わせ内容</p>
              <p>{formData.subject}</p>
            </div>
            <div>
              <p className="font-semibold text-emerald-900">メッセージ</p>
              <p className="whitespace-pre-wrap">{formData.message}</p>
            </div>
          </div>
          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="px-6 py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              修正する
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              className="px-6 py-3 bg-emerald-700 text-white hover:bg-emerald-800 transition-colors"
            >
              この内容で送信する
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'submitting'}>
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full border-4 border-emerald-100 border-t-emerald-700 animate-spin" />
          <h3 className="text-2xl font-bold text-emerald-900 mb-3">送信中です</h3>
          <p className="text-gray-600">内容を送信しています。しばらくお待ちください。</p>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'success'}>
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
            <i className="ri-check-line text-3xl text-emerald-700"></i>
          </div>
          <h3 className="text-2xl font-bold text-emerald-900 mb-3">送信が完了しました</h3>
          <p className="text-gray-600">
            お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
          </p>
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="mt-8 px-6 py-3 bg-emerald-700 text-white hover:bg-emerald-800 transition-colors"
          >
            閉じる
          </button>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'error'}>
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-close-line text-3xl text-red-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">送信できませんでした</h3>
          <p className="text-gray-600">{errorMessage}</p>
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="mt-8 px-6 py-3 bg-gray-900 text-white hover:bg-gray-800 transition-colors"
          >
            戻る
          </button>
        </div>
      </Modal>
    </section>
  );
}
